import * as amplitude from '@amplitude/analytics-browser';
import { COMPONENT_VIEWED_EVENT_NAME } from './constants';
import { getAmplitudeInitialized } from './amplitude-state';
import { capturePageAction, ActionSeverity } from '@chegg/observability';
// Re-export the constant for external use
export { COMPONENT_VIEWED_EVENT_NAME };
/**
 * Tracks a viewed event through Amplitude
 *
 * @param eventName - The name of the event to track
 * @param properties - The properties to include with the event
 * @returns true if the event was tracked, false otherwise
 */ export function trackViewedEvent(eventName = COMPONENT_VIEWED_EVENT_NAME, properties = {}) {
    // Check if Amplitude is initialized using our state tracking
    if (!getAmplitudeInitialized()) {
        capturePageAction({
            action: 'Amplitude not initialized. Component view event not tracked.',
            severity: ActionSeverity.WARNING,
            location: 'amplitude-analytics'
        });
        return false;
    }
    try {
        // Track the event with Amplitude
        amplitude.track(eventName, properties);
        return true;
    } catch (error) {
        capturePageAction({
            action: 'Failed to track event',
            severity: ActionSeverity.WARNING,
            location: 'amplitude-analytics'
        }, {
            error: String(error)
        });
        return false;
    }
}
/**
 * Creates an IntersectionObserver to track when an element is viewed
 *
 * @param element - The element to observe
 * @param onViewed - Callback function to run when the element is intersecting
 * @param options - Options for configuring the IntersectionObserver
 * @returns A cleanup function to disconnect the observer
 */ export function createElementViewedObserver(element, onViewed, options = {}) {
    const { threshold = 0.1, trackOnce = true } = options;
    // Track if the element has been observed
    let hasBeenObserved = false;
    // Create observer
    const observer = new IntersectionObserver((entries)=>{
        for (const entry of entries){
            if (entry.isIntersecting) {
                // Check BEFORE calling onViewed if trackOnce is enabled
                if (trackOnce && hasBeenObserved) {
                    continue;
                }
                // Call the view handler only if not already tracked (when trackOnce=true)
                onViewed();
                // Mark as observed and unobserve if trackOnce is true
                if (trackOnce) {
                    hasBeenObserved = true;
                    observer.unobserve(element);
                }
            }
        }
    }, {
        threshold
    });
    // Start observing the element
    observer.observe(element);
    // Return cleanup function
    return ()=>{
        observer.disconnect();
    };
}
/**
 * Implementation of trackComponentViewed with flexible parameters
 */ export function trackComponentViewed(elementOrConfig, componentName, props = {}) {
    // Handle both object and parameter-based signatures
    let element;
    let finalComponentName;
    let finalProps;
    let options;
    if (typeof elementOrConfig === 'object' && 'element' in elementOrConfig) {
        // Object-based signature
        const config = elementOrConfig;
        element = config.element;
        finalComponentName = config.componentName;
        finalProps = config.props || {};
        options = config.options || {};
    } else {
        // Parameter-based signature (legacy)
        element = elementOrConfig;
        finalComponentName = componentName;
        finalProps = props;
        options = {};
    }
    const { eventName = COMPONENT_VIEWED_EVENT_NAME, eventProperties = {}, trackOnce = true, threshold = 0.1 } = options;
    let hasTracked = false;
    const handleIntersection = ()=>{
        // Explicitly check against true since trackOnce could be undefined
        if (trackOnce && hasTracked) {
            return;
        }
        // Only include component_props if props were actually provided
        const propsToInclude = Object.keys(finalProps).length > 0 ? {
            component_props: finalProps
        } : {};
        // Track the event
        trackViewedEvent(eventName, {
            component_name: finalComponentName,
            ...propsToInclude,
            ...eventProperties
        });
        hasTracked = true;
    };
    return createElementViewedObserver(element, handleIntersection, {
        threshold,
        trackOnce
    });
}
/**
 * Creates an element viewed tracker using Intersection Observer
 * This is the non-React version for vanilla JS usage
 */ export function createElementViewedTracker(element, eventName = COMPONENT_VIEWED_EVENT_NAME, properties = {}) {
    let hasBeenViewed = false;
    const observer = new IntersectionObserver((entries)=>{
        entries.forEach((entry)=>{
            if (entry.isIntersecting && !hasBeenViewed) {
                hasBeenViewed = true;
                trackViewedEvent(eventName, properties);
                observer.disconnect();
            }
        });
    }, {
        threshold: 0.1
    });
    observer.observe(element);
    // Return cleanup function
    return ()=>{
        observer.disconnect();
    };
}
/**
 * Simple function to track component viewed event immediately
 */ export function trackComponentViewedImmediate(componentName, additionalProperties = {}) {
    return trackViewedEvent(COMPONENT_VIEWED_EVENT_NAME, {
        component_name: componentName,
        ...additionalProperties
    });
}
